import { Box, Center, Flex, Spinner, Table, Text } from '@chakra-ui/react';
import { FaPaperclip } from 'react-icons/fa';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useIncidentEvents } from '../../../hooks/queries/useIncidents';
import type { IncidentEvent } from '../../../services/incidents.service';

dayjs.extend(utc);

const EVENT_TYPE_LABEL: Record<IncidentEvent['event_type'], string> = {
  chat: 'Chat',
  system: 'System',
  guidance: 'Guidance',
};

function formatTimestamp(iso: string): string {
  if (!iso) return '';
  return dayjs.utc(iso).local().format('M/D/YYYY, h:mm A');
}

interface IncidentEventsTableProps {
  incidentId: number;
}

export function IncidentEventsTable({ incidentId }: IncidentEventsTableProps) {
  const { data, isLoading, isError } = useIncidentEvents(incidentId);

  if (isLoading) {
    return (
      <Center py={6}>
        <Spinner size="md" color="button.primary" />
      </Center>
    );
  }

  if (isError) {
    return (
      <Center py={6}>
        <Text color="text.secondary">Couldn't load incident events.</Text>
      </Center>
    );
  }

  const rows = data?.results ?? [];

  return (
    <Box
      border="1px solid"
      borderColor="border.subtle"
      borderRadius="md"
      overflow="hidden"
    >
      {rows.length === 0 ? (
        <Center py={10}>
          <Text color="text.secondary">No events recorded for this incident.</Text>
        </Center>
      ) : (
        <Table.Root>
          <Table.Header bg="surface.input">
            <Table.Row bg="surface.searchInput">
              {[
                { title: 'Event' },
                { title: 'Type' },
                { title: 'Author' },
                { title: 'Content' },
                { title: <FaPaperclip />, isIcon: true },
              ].map((column, idx) => (
                <Table.ColumnHeader
                  key={idx}
                  color="text.secondary"
                  fontWeight={600}
                  fontSize="md"
                  borderBottom="1px solid"
                  borderRight="1px solid"
                  _last={{ borderRight: 'none' }}
                  borderColor="border.line"
                  width={column.isIcon ? '90px' : 'auto'}
                  textTransform="capitalize"
                >
                  <Flex alignItems="center">
                    <Text mr={1}>{column.title}</Text>
                  </Flex>
                </Table.ColumnHeader>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body color="surface.primary">
            {rows.map((row) => (
              <Table.Row
                key={row.id}
                background="surface.primary"
                borderColor="border.line"
                borderBottom="1px solid"
              >
                <Table.Cell color="text.primary">
                  {formatTimestamp(row.created_at)}
                </Table.Cell>
                <Table.Cell color="text.primary">
                  {EVENT_TYPE_LABEL[row.event_type] ?? row.event_type}
                </Table.Cell>
                <Table.Cell color="text.primary">{row.author_name}</Table.Cell>
                <Table.Cell color="text.primary">{row.content}</Table.Cell>
                <Table.Cell color="text.primary">
                  {row.has_attachments ? <FaPaperclip /> : null}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
    </Box>
  );
}
